import SourceFlowImage from "@sourceflow-uk/sourceflowimage";
import {
  Container,
  Nav,
  NavItem,
  Row,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import styles from "./styles.module.scss";
import Link from "next/link";
import Logo from "/public/site-assets/svg/logo-black.svg";
import NewNavigationUK from "../../.sourceflow/new_navigation_uk.json";
import NewNavigationUS from "../../.sourceflow/new_navigation_us.json";
import React, { useState, useEffect } from "react";
import SearchIcon from "/public/site-assets/svg/black-magnifying-glass.svg";
import CloseMark from "/public/site-assets/svg/close-mark.svg";
import Telephone from "/public/site-assets/svg/telephone.svg";
import News from "../../.sourceflow/news.json";
import PodcastsData from "../../.sourceflow/podcasts.json";
import DynamicPagesData from "../../.sourceflow/dynamic_pages.json";
import Fuse from "fuse.js";
import clsx from "classnames";
import { useRouter } from "next/router";
import PoweredByBanner from "../PoweredByBanner";

import UKFlag from "/public/site-assets/svg/uk-flag.svg";
import USFlag from "/public/site-assets/svg/us-flag.svg";

export default function MainHeader() {
  const router = useRouter();
  // UK and US homepages both carry the PoweredByBanner.
  const currentPath = router.asPath.split(/[?#]/)[0].replace(/\/$/, "");
  const isHomepage = router.pathname === "/" || currentPath === "/us";

  const dropdownRefs = {};

  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const toggleSearch = () => {
    if (isSearchOpen) {
      setSearchQuery("");
      setSearchResults([]);
    }
    setIsSearchOpen(!isSearchOpen);
  };

  let selectedNavigation;
  if (router.asPath.startsWith("/us")) {
    selectedNavigation = NewNavigationUS;
  } else {
    selectedNavigation = NewNavigationUK;
  }

  const ParentMainNavigation = selectedNavigation.category_values.filter(
    (post) => post.parent.identifier === null
  );
  const ChildrenMainNavigation = selectedNavigation.category_values.filter(
    (post) => post.parent.identifier !== null
  );

  const [activeDropdown, setActiveDropdown] = useState(null);

  const newToggleDropdown = (titleEn) => {
    document.querySelectorAll("[data-parent-id]").forEach((el) => {
      el.classList.remove("active");
    });

    setActiveDropdown(activeDropdown === titleEn ? null : titleEn);

    const parentElement = document.querySelector(
      `[data-parent-id="${titleEn}"]`
    );

    if (parentElement) {
      const isActive = parentElement.classList.contains("active");

      document.querySelectorAll("[data-parent-id]").forEach((el) => {
        el.classList.remove("active");
      });

      if (!isActive) {
        parentElement.classList.add("active");
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("[data-parent-id]") &&
        !event.target.closest(".dropdown") &&
        !event.target.closest(".nav")
      ) {
        setActiveDropdown(null);

        document.querySelectorAll("[data-parent-id]").forEach((el) => {
          el.classList.remove("active");
        });
      }
    };

    document.addEventListener("mouseover", handleClickOutside);
    return () => {
      document.removeEventListener("mouseover", handleClickOutside);
    };
  }, []);

  const AllInsights = News.category_values.filter(function (post) {
    return (
      post.country?.en ==
        (router.asPath.startsWith("/us")
          ? "United States"
          : "United Kingdom") || post.country?.en == "All"
    );
  });

  const featuredPost = AllInsights?.filter(
    (post) => post?.featured_on_navigation?.en === true
  ).slice(0, 1);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const isUS = router.asPath.startsWith("/us");
  const prefix = isUS ? "/us" : "";

  const staticPages = [
    { title: "Insights", url: `${prefix}/blog`, category: "Page" },
    { title: "Podcasts", url: `${prefix}/podcasts`, category: "Page" },
    { title: "Meet the Team", url: `${prefix}/about-us/meet-the-team`, category: "Page" },
    ...(isUS
      ? [
          { title: "Salary Survey", url: "/us/salary-survey", category: "Page" },
        ]
      : [
          { title: "Career Advice", url: "/career-advice", category: "Page" },
          { title: "Synergy Scientific Solutions", url: "/employers/synergy-scientific-solutions", category: "Page" },
          { title: "Global Talent Trends and Insights 2024", url: "/global-talent-trends-and-insights-2024", category: "Page" },
        ]),
  ];

  const insightPages = AllInsights.map((post) => ({
    title: post.title.en,
    url: `${prefix}/blog/${post.url_slug}`,
    category: "Insight",
  }));

  const podcastPages = PodcastsData.category_values
    .filter((post) => {
      const countries = post.country?.en;
      if (!countries) return true;
      if (Array.isArray(countries)) {
        return isUS
          ? countries.includes("United States") || countries.includes("All")
          : countries.includes("United Kingdom") || countries.includes("All");
      }
      return true;
    })
    .map((post) => ({
      title: post.title.en,
      url: `${prefix}/podcasts/${post.url_slug}`,
      category: "Podcast",
    }));

  const excludedSlugs = new Set(["/", "page-builder"]);
  const dynamicPages = Object.values(DynamicPagesData)
    .filter((page) => {
      if (page.is_draft) return false;
      if (excludedSlugs.has(page.url_slug)) return false;
      if (page.url_slug.includes("submission-success")) return false;
      const isUSPage = page.url_slug === "us" || page.url_slug.startsWith("us/");
      return isUS ? isUSPage : !isUSPage;
    })
    .map((page) => ({
      title: page.title,
      url: `/${page.url_slug}`,
      category: "Page",
    }));

  const allPages = [...staticPages, ...insightPages, ...podcastPages, ...dynamicPages];

  const fuse = new Fuse(allPages, {
    includeScore: true,
    keys: ["title"],
    threshold: 0.4,
  });

  const handleSearch = (query) => {
    if (query) {
      setSearchResults(fuse.search(query));
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    handleSearch(query);
  };

  const [activeParent, setActiveParent] = useState(null);
  const handleParentClick = (parentTitle) => {
    setActiveParent((prev) => (prev === parentTitle ? null : parentTitle));
  };

  useEffect(() => {
    const enableReciteButton = document.getElementById("reciteEnable");

    if (enableReciteButton) {
      enableReciteButton.addEventListener("click", window.loadService);
    }

    return () => {
      if (enableReciteButton) {
        enableReciteButton.removeEventListener("click", window.loadService);
      }
    };
  }, []);

  return (
    <>
      <section
        className={`${styles.root} main-navigation ${isOpen ? "active" : ""}`}
      >
        <div className="reciteWrapper py-2 bg-white">
          <Container>
            <button
              className="recite-btn p-0"
              id="reciteEnable"
              aria-label="Accessibility and Languages"
            >
              Accessibility and Languages
            </button>
          </Container>
        </div>
        {isHomepage && <PoweredByBanner />}
        <Container className="position-relative mainWrapper">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Link href={`${router.asPath.startsWith("/us") ? "/us" : "/"}`}>
                <a className="logo me-md-4" aria-label="SRG" title="SRG">
                  <Logo width={120} height={120} />
                </a>
              </Link>
              <Nav className="d-none d-lg-flex">
                {ParentMainNavigation.map((post, index) => (
                  <NavItem
                    key={index}
                    className={`parent mx-1 m-xxxl-2 ${
                      activeDropdown === post?.title.en ? "active" : ""
                    }`}
                  >
                    <button
                      className={`dropdown ${
                        post.parent.identifier === null ? "has-children" : null
                      }`}
                      aria-label={`${post?.title.en}`}
                      onMouseEnter={() => newToggleDropdown(post?.title.en)}
                      ref={(ref) => (dropdownRefs[post?.title.en] = ref)}
                    >
                      {post?.title.en}
                    </button>
                  </NavItem>
                ))}
              </Nav>
            </div>
            <div className="d-flex align-items-center d-md-flex">
              <UncontrolledDropdown className="lang_select d-none d-lg-flex">
                <DropdownToggle aria-label="Language">
                  <span
                    className={
                      router.asPath.startsWith("/") &&
                      !router.asPath.startsWith("/us")
                        ? "active"
                        : ""
                    }
                  >
                    <UKFlag />
                  </span>
                  <span
                    className={router.asPath.startsWith("/us") ? "active" : ""}
                  >
                    <USFlag />
                  </span>
                </DropdownToggle>

                <DropdownMenu aria-labelledby="dropdownMenuButton">
                  <DropdownItem
                    href="/"
                    title={`United Kingdom`}
                    aria-label={`United Kingdom`}
                    className={
                      router.asPath.startsWith("/") &&
                      !router.asPath.startsWith("/us")
                        ? "active"
                        : ""
                    }
                  >
                    <UKFlag />
                  </DropdownItem>
                  <DropdownItem
                    href="/us"
                    title={`United States`}
                    aria-label={`United States`}
                    className={router.asPath.startsWith("/us") ? "active" : ""}
                  >
                    <USFlag />
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              <button
                className="search-icon d-flex d-none d-lg-inline-flex"
                aria-label="Search"
                onClick={toggleSearch}
              >
                <SearchIcon />
              </button>
              
              <Link
                href={`${
                  router.asPath.startsWith("/us")
                    ? "/us/get-hired"
                    : "/get-hired"
                }`}
              >
                <a className="primaryBtn no-arrow text-nowrap m-0 d-none d-lg-inline-block ms-2">
                  Get Hired
                </a>
              </Link>

              <Link
                href={`${
                  router.asPath.startsWith("/us")
                    ? "/us/hire-now"
                    : "/hire-now"
                }`}
              >
                <a className="secondaryBtn no-arrow text-nowrap m-0 ms-2 d-none d-lg-inline-block">
                  Hire Now
                </a>
              </Link>

              <button
                aria-label="Menu"
                className={`d-inline-block d-lg-none mobile-menu ms-2 position-relative ${
                  isOpen ? "active" : ""
                }`}
                onClick={() => setIsOpen((isOpen) => !isOpen)}
              >
                {!isOpen ? "Menu" : "Close"}
              </button>
            </div>
          </div>
        </Container>
        {ParentMainNavigation.length > 0 && (
          <div className="d-none d-lg-block">
            {ParentMainNavigation.map((parentPost, parentIndex) => {
              const childrenForParent = ChildrenMainNavigation.filter(
                (childPost) => childPost.parent.id === parentPost.id
              );
              if (childrenForParent.length === 0) {
                return null;
              }
              return (
                <div
                  className={clsx("dropdownItem")}
                  data-parent-id={parentPost.title.en}
                  key={parentIndex}
                >
                  <div className="d-flex">
                    <div className="left-column col-4 p-4">
                      {parentPost.title.en != "Insights" && (
                        <div>
                          <a href={parentPost.button_url.en}>
                            <SourceFlowImage
                              src={
                                parentPost.image?.en ??
                                "/site-assets/placeholders/img-placeholder.jpg"
                              }
                              size="500x250"
                              alt={`${parentPost.title.en}`}
                              className={`mb-4 featured-image w-100`}
                            />
                          </a>
                          <div className="mb-4 post-description">
                            <h5 className="fw-bold text-white">
                              {parentPost.title.en}
                            </h5>
                            <p>{parentPost.subtitle.en}</p>
                          </div>
                        </div>
                      )}
                      {parentPost.title.en === "Insights" && (
                        <div>
                          {featuredPost && (
                            <div>
                              <div className="featured-insights-image-wrapper position-relative">
                                <div>
                                  {featuredPost.map((post) => (
                                    <a
                                      key={post.id}
                                      href={
                                        post.external_campaign
                                          ? post.external_campaign.en
                                          : `${
                                              router.asPath.startsWith("/us")
                                                ? "/us"
                                                : ""
                                            }${
                                              post?.what_is_template
                                                ? "/what-is-outsourcing/"
                                                : post.gated_content_template
                                                ? "/gated-content/"
                                                : post.success_story_template
                                                ? "/resources/customer-success-stories/"
                                                : "/blog/"
                                            }${post.url_slug}`
                                      }
                                      target={
                                        post.external_campaign
                                          ? "_blank"
                                          : "_self"
                                      }
                                    >
                                      <SourceFlowImage
                                        src={post.image.en}
                                        size="500x250"
                                        alt={`${post.title.en}`}
                                        className={`mb-4 featured-image w-100`}
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                              {featuredPost.map((post) => (
                                <a
                                  key={post.id}
                                  href={
                                    post.external_campaign
                                      ? post.external_campaign.en
                                      : `${
                                          router.asPath.startsWith("/us")
                                            ? "/us"
                                            : ""
                                        }${
                                          post?.what_is_template
                                            ? "/what-is-outsourcing/"
                                            : post.gated_content_template
                                            ? "/gated-content/"
                                            : post.success_story_template
                                            ? "/resources/customer-success-stories/"
                                            : "/blog/"
                                        }${post.url_slug}`
                                  }
                                  target={
                                    post.external_campaign ? "_blank" : "_self"
                                  }
                                >
                                  <h5 className="fw-bold text-white">
                                    {parentPost.subtitle.en}
                                  </h5>
                                  <div className="mb-4 post-description post-blog">
                                    {post.title.en}
                                  </div>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="primaryBtn-wrapper m-0 w-100 justify-content-center">
                        <p>
                          <a href={parentPost.button_url.en}>
                            {parentPost.button_title.en}
                          </a>
                        </p>
                      </div>
                    </div>
                    <div className="right-column p-4 w-100">
                      <div className="wrapper">
                        {childrenForParent.map((childPost) => (
                          <div className="child-nav" key={childPost.id}>
                            <div>
                              {childPost.new_tab?.en ? (
                                <a
                                  className="sub-item-parent post-title"
                                  href={childPost.url.en}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {childPost.title.en}
                                </a>
                              ) : !childPost.url.en ||
                                childPost.url.en === "#" ? (
                                <div className="sub-item-parent no-link post-title">
                                  {childPost.title.en}
                                </div>
                              ) : (
                                <a
                                  className="sub-item-parent post-title"
                                  href={childPost.url.en}
                                >
                                  {childPost.title.en}
                                </a>
                              )}
                              {childPost.subtitle?.en && (
                                <div className="post-subtitle mb-4 mt-2">
                                  {childPost.subtitle.en}
                                </div>
                              )}
                            </div>
                            {ChildrenMainNavigation.filter(
                              (sub_childPost) =>
                                sub_childPost.parent.id === childPost.id
                            ).map((sub_childPost) => (
                              <div key={sub_childPost.id}>
                                <a
                                  className="sub-item post-title"
                                  href={sub_childPost.url.en}
                                  target={sub_childPost.new_tab?.en ? "_blank" : "_self"}
                                  rel={sub_childPost.new_tab?.en ? "noopener noreferrer" : ""}
                                >
                                  {sub_childPost.title.en}
                                </a>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
      <div
        className={`${styles.root} mobile-dropdown ${
          isOpen ? "active" : "overflow-hidden"
        } ${isHomepage ? "has-powered-by-banner" : ""} d-inline-block d-lg-none`}
      >
        <div className="d-flex flex-wrap justify-content-between mobileWrapper">
          <div className="w-100">
            <Nav className={`d-flex flex-wrap`}>
              {!activeParent && (
                <>
                  <NavItem className={`parent w-100`}>
                    <a href="/">Home</a>
                  </NavItem>

                  {ParentMainNavigation.map((post, index) => (
                    <NavItem key={index} className={`parent w-100`}>
                      <button
                        className={`bg-transparent dropdown w-100 text-start ${
                          post.parent.identifier === null
                            ? "has-children"
                            : null
                        }`}
                        aria-label={`${post?.title.en}`}
                        onClick={() => handleParentClick(post.title.en)}
                      >
                        <strong>{post?.title.en}</strong>
                      </button>
                    </NavItem>
                  ))}
                  
                  <div className="mobile-get-hire-buttons d-flex gap-2">
                    <Link
                      href={`${
                        router.asPath.startsWith("/us")
                          ? "/us/get-hired"
                          : "/get-hired"
                      }`}
                    >
                      <a className="primaryBtn no-arrow text-nowrap m-0">
                        Get Hired
                      </a>
                    </Link>
                    <Link
                      href={`${
                        router.asPath.startsWith("/us")
                          ? "/us/hire-now"
                          : "/hire-now"
                      }`}
                    >
                      <a className="secondaryBtn no-arrow text-nowrap m-0">
                        Hire Now
                      </a>
                    </Link>
                  </div>
                </>
              )}
              {activeParent && (
                <NavItem className={`parent w-100 return-btn-wrapper`}>
                  <button
                    className={`border-0 bg-transparent px-0 dropdown w-100 text-start`}
                    onClick={() => setActiveParent(null)}
                  >
                    Back
                  </button>
                </NavItem>
              )}
            </Nav>
            {ParentMainNavigation.length > 0 && (
              <div className="sub-children-wrapper">
                {ParentMainNavigation.map((parentPost) => {
                  const childrenForParent = ChildrenMainNavigation.filter(
                    (childPost) => childPost.parent.id === parentPost.id
                  );
                  if (childrenForParent.length === 0) {
                    return null;
                  }
                  return (
                    <div
                      key={parentPost.id}
                      className={clsx("dropdownItem", {
                        active: activeParent === parentPost.title.en,
                      })}
                      data-mobile-parent-id={parentPost.title.en}
                    >
                      {(parentPost.title.en === "Employers" ||
                        parentPost.title.en === "Specialism" ||
                        parentPost.title.en === "Markets") && (
                        <div className="sub-children-title">
                          {parentPost.title.en}
                        </div>
                      )}
                      <div className="wrapper">
                        {(parentPost.title.en === "About Us") && (
                          <div className="child-nav">
                            <a
                              className="sub-item-parent post-title"
                              href={parentPost.url.en}
                            >
                              {parentPost.title.en}
                            </a>
                          </div>
                        )}
                        {childrenForParent.map((childPost) => (
                          <div className="child-nav" key={childPost.id}>
                            <div>
                              <a
                                className="sub-item-parent post-title"
                                href={childPost.url.en}
                              >
                                {childPost.title.en}
                              </a>
                              {ChildrenMainNavigation.filter(
                                (sub_childPost) =>
                                  sub_childPost.parent.id === childPost.id
                              ).map((sub_childPost) => (
                                <div
                                  className="sub-item-child"
                                  key={sub_childPost.id}
                                >
                                  <a
                                    className="sub-item"
                                    href={sub_childPost.url.en}
                                  >
                                    {sub_childPost.title.en}
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="mobile-language w-100 d-flex">
            <div className="w-100 mb-4">
              <hr />
              <Container>
                <UncontrolledDropdown className="lang_select">
                <DropdownToggle>
                  <span className={!router.asPath.startsWith("/us") ? "active" : ""}>
                    <UKFlag /> UK
                  </span>
                  <span className={router.asPath.startsWith("/us") ? "active" : ""}>
                    <USFlag /> US
                  </span>
                </DropdownToggle>

                <DropdownMenu aria-labelledby="dropdownMenuButton">
                  <DropdownItem
                    href="/"
                    title="United Kingdom"
                    className={!router.asPath.startsWith("/us") ? "active" : ""}
                  >
                    <UKFlag /> UK
                  </DropdownItem>
                  <DropdownItem
                    href="/us"
                    title="United States"
                    className={router.asPath.startsWith("/us") ? "active" : ""}
                  >
                    <USFlag /> US
                  </DropdownItem>
                </DropdownMenu>
                </UncontrolledDropdown>
              </Container>
            </div>
          </div>
        </div>
      </div>
      {isSearchOpen && (
        <section className={`${styles.root} site-search d-none d-md-block`}>
          <Container>
            <div className="d-flex align-items-center">
              <div className="w-100 position-relative filters">
                <input
                  type="text"
                  id="search"
                  placeholder="Site Search..."
                  onChange={handleSearchChange}
                  value={searchQuery}
                />
                <span className="icon d-inline-block position-absolute">
                  <SearchIcon />
                </span>
              </div>
              <button
                className={`primaryBtn no-arrow border-0 m-0 ms-3`}
                aria-label="Search"
                onClick={() => toggleSearch()}
              >
                <CloseMark />
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-4 search-results">
                <p className="fs-4">
                  <strong>Search Results:</strong>
                </p>
                <ul>
                  {searchResults.map((result, index) => (
                    <li key={index}>
                      <Link href={result.item.url}>
                        <a onClick={() => toggleSearch()}>
                          {result.item.title}
                        </a>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Container>
        </section>
      )}
    </>
  );
}